import decode from 'jwt-decode'
import { API_URL } from 'constants/config'
import strings from 'strings'
import history from 'tools/history'

const dashboard = { id: 'dashboard', label: 'Dashboard', link: '/dashboard', icon: 'dashboard', type: 'parent' }
const quickSearch = { id: 'quick-search', label: 'Quick Search', link: '/quick-search', icon: 'quickSearch', type: 'parent' }
const applicants = { id: 'applicants', label: 'Applications', link: '/applicants', icon: 'applications', type: 'parent' }
const applicationOffers = { id: 'application-offers', label: 'Application Offers', link: '/application-offers', icon: 'applications', type: 'parent' }
const ucc = { id: 'ucc', label: 'UCCs', link: '/ucc', icon: 'clients', type: 'parent' }
const brokerPortal = {
  id: 'broker-portal',
  label: 'Broker Dashboard',
  link: '/broker-portal',
  icon: 'brokerDashboard',
  type: 'parent'
}
const underwriterDashboard = {
  id: 'underwriter-dashboard',
  label: 'Activity Dashboard',
  link: '/underwriter-dashboard',
  icon: 'brokerDashboard',
  type: 'parent'
}

/** Settings and manageCsv are commented for now, but needed for future use */
// const settings = { id: 'settings', label: 'Settings', link: '/settings', icon: 'settings', type: 'children' }
// const manageCsv = { id: 'manage-csv', label: 'CSV Uploads', link: '/manage-csv', icon: 'csv', type: 'parent' }

const help = { id: 'help', label: 'Help', link: 'https://support.roxwrite.com/hc/en-us', icon: 'help', type: 'children' }
const profile = { id: 'profile', label: 'Profile', link: '/profile', icon: 'profile', type: 'children' }

class AuthService {
  constructor() {
    this.login = this.login.bind(this)
    this.getProfile = this.getProfile.bind(this)
  }

  get selectedClient() {
    return localStorage.getItem('selectedClient')
  }

  set selectedClient(selectedClient) {
    if (selectedClient === null) {
      return localStorage.removeItem('selectedClient')
    }

    localStorage.setItem('selectedClient', selectedClient)
  }

  get loggedInUser() {
    return this.user ? this.user : {}
  }

  set loggedInUser(user) {
    if (user) {
      this.user = user
    }
  }

  /**
   * Returns roleName from the loggedInUser securityContext
   */
  getRoleName() {
    return this.loggedInUser?.securityContext?.roles[0]?.roleName
  }

  /**
   * Check if the logged in user has the given role
   * 
   * @param {*} roleName 
   */
  hasRole(roleName) {
    return this.loggedInUser?.securityContext?.roles?.some(role => role.roleName === roleName)
  }

  /**
   * Check if the logged in user has any of the given roles.
   * 
   * @param  {...any} roleNames 
   */
  hasAnyRole(...roleNames) {
    return !roleNames ? false : (roleNames.some(roleName => this.hasRole(roleName)))
  }

  /**
   * Check if the user cannot do any actions
   */
  isReadOnlyUser() {
    return this.hasRole('READ_ONLY_USER')
  }

  /**
   * Check if the logged in user is a broker customer
   */
  isBrokerCustomer() {
    return this.hasRole('BROKER_CUSTOMER')
  }

  /**
   * Only client admins and Rox admins add, enable/disable and edit users (underwriters also hold MANAGE_USER)
   */
  canManageUsers() {
    return this.hasAnyRole('ROX_ADMIN', 'CUSTOMER_ADMIN')
  }

  /**
   * Check if the logged in user is a sales rep: they only see the applications shared with them
   */
  isSalesRep() {
    return this.hasRole('SALES_REP')
  }

  /**
   * Check if the logged in user can list broker applications
   */
  canListBrokerApplications() {
    return this.hasPrivilege('BROKER_LIST_APPLICATIONS')
  }

  canWriteStripe() {
    return this.hasPrivilege('STRIPE_WRITE')
  }

  /**
   * Check if the logged in user has any role starts with the role prefix.
   * 
   * @param {*} rolePrefix 
   */
  hasRolePrefix(rolePrefix) {
    return this.loggedInUser?.securityContext?.roles?.some(role => role.roleName.startsWith(rolePrefix))
  }

  /**
   * Check if the logged in user has any of the given role prefixes.
   * 
   * @param  {...any} rolePrefixes 
   */
  hasAnyRolePrefix(...rolePrefixes) {
    return !rolePrefixes ? false : (rolePrefixes.some(rolePrefix => this.hasRolePrefix(rolePrefix)))
  }

  /**
   * Check if the logged in user has the given privilege
   * 
   * @param {*} privilegeName 
   */
  hasPrivilege(privilegeName) {
    return this.loggedInUser?.securityContext?.privileges?.some(privilege => privilege.privilegeName === privilegeName)
  }

  hideDecisioning() {
    return this.loggedInUser?.securityContext?.client?.hideDecisioning
  }

  /**
   * Check if the logged in user has any of the given privileges.
   * 
   * @param  {...any} privilegeNames 
   */
  hasAnyPrivilege(...privilegeNames) {
    return !privilegeNames ? false : (privilegeNames.some(privilegeName => this.hasPrivilege(privilegeName)))
  }

  /**
   * Check if the logged in user has the given privilege prefix
   * 
   * @param {*} privilegePrefix 
   */
  hasPrivilegePrefix(privilegePrefix) {
    return this.loggedInUser?.securityContext?.privileges?.some(privilege => privilege.privilegeName.startsWith(privilegePrefix))
  }

  /**
   * Check if the logged in user has any of the given privilege prefixes.
   * 
   * @param  {...any} privilegePrefixes 
   */
  hasAnyPrivilegePrefix(...privilegePrefixes) {
    return !privilegePrefixes ? false : (privilegePrefixes.some(privilegePrefix => this.hasPrivilegePrefix(privilegePrefix)))
  }

  /**
   * Checks and returns the menubarlist based on the roles
   */
  buildMenubarList(defaultMenuBarList) {
    const roleName = this.getRoleName()
    const roleStrings = strings.ROLES
    const { gmailPdfImportEnabled, isReadOnlyUser, activityDashboardEnabled } = this.getClientFeatures()
    let menuList = []
    switch (roleName) {
      case roleStrings.UNDER_WRITER:
        menuList = [dashboard, underwriterDashboard, quickSearch, applicants, applicationOffers, ucc, help, profile]
        break;
      case roleStrings.BROKER_CUSTOMER:
        menuList = [brokerPortal, help, profile]
        break;
      default:
        menuList = [...defaultMenuBarList]
        if (this.hasRole('BROKER_CUSTOMER')) {
          menuList = [brokerPortal, help, profile]
        }
        break;
    }
    if(!gmailPdfImportEnabled) {
      menuList = menuList.filter(item => item.id !== 'email-logs');
    }
    if(!activityDashboardEnabled) {
      menuList = menuList.filter(item => item.id !== 'underwriter-dashboard');
    }
    if(isReadOnlyUser) {
      menuList = menuList.filter(item => item.id !== 'quick-search');
    }
    if (this.isSalesRep()) {
      menuList = [applicants, profile]
    }

    return menuList
  }

  /** ***** Login *****
   * Gets a token from the API server using the fetch API
   */
  login(username, password) {
    return new Promise((resolve, reject) => {
      fetch(`${API_URL}/login`, {
        method: 'POST',
        body: JSON.stringify({
          username,
          password
        })
      })
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            return response.json()
          }
          throw response
        })
        .then(parsedResponse => {
          // Client with two-factor in force: a correct password answers with a challenge
          // instead of a session (still HTTP 200, so the branch is on the body). No tokens
          // to store yet, verifyTwoFactor issues them once the emailed code checks out.
          if (parsedResponse.twoFactorRequired) {
            resolve(parsedResponse)
            return
          }
          this.setToken(parsedResponse['Authorization'])
          this.setRefreshToken(parsedResponse['Refresh_token'])
          resolve(parsedResponse)
        })
        .catch(err => {
          reject(err)
        })
    })
  }


  /**
   * Second step of a two-factor login: the challenge token proves the password step was
   * cleared, the code proves the mailbox. Only together do they produce a session.
   */
  verifyTwoFactor(challengeToken, code) {
    return new Promise((resolve, reject) => {
      fetch(`${API_URL}/login/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ challengeToken, code: code.trim().toUpperCase() })
      })
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            return response.json()
          }
          throw response
        })
        .then(parsedResponse => {
          this.setToken(parsedResponse['Authorization'])
          this.setRefreshToken(parsedResponse['Refresh_token'])
          resolve(parsedResponse)
        })
        .catch(reject)
    })
  }


  /** Sends a fresh code for a two-factor challenge that is still alive. */
  resendTwoFactor(challengeToken) {
    return new Promise((resolve, reject) => {
      fetch(`${API_URL}/login/resend-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ challengeToken })
      })
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            return response.json()
          }
          throw response
        })
        .then(resolve)
        .catch(reject)
    })
  }


  requestOtp(email) {
    return new Promise((resolve, reject) => {
      fetch(`${API_URL}/login/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      })
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            return response.json()
          }
          throw response
        })
        .then(resolve)
        .catch(reject)
    })
  }


  verifyOtp(email, code) {
    return new Promise((resolve, reject) => {
      fetch(`${API_URL}/login/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, code: code.trim().toUpperCase() })
      })
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            return response.json()
          }
          throw response
        })
        .then(parsedResponse => {
          // An account with no password under a two-factor client gets no session here, only
          // a setupToken and passwordSetupRequired: it owes us a password first. Storing the
          // missing Authorization would leave "undefined" as the session token.
          if (parsedResponse['Authorization']) {
            this.setToken(parsedResponse['Authorization'])
            this.setRefreshToken(parsedResponse['Refresh_token'])
          }
          resolve(parsedResponse)
        })
        .catch(reject)
    })
  }


  /**
   * Creates the first password of an account from a verified login code. It never replaces an
   * existing one: changing a password goes through the profile or "forgot my password".
   */
  setInitialPassword(setupToken, password) {
    return new Promise((resolve, reject) => {
      fetch(`${API_URL}/login/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ setupToken, password })
      })
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            return response.json()
          }
          throw response
        })
        .then(parsedResponse => {
          // Creating the password bumps the token version, so any session issued alongside the
          // setup token is already dead. These are the tokens that count.
          this.setToken(parsedResponse['Authorization'])
          this.setRefreshToken(parsedResponse['Refresh_token'])
          resolve(parsedResponse)
        })
        .catch(reject)
    })
  }

  
  registerOtp(email) {
    return new Promise((resolve, reject) => {
      fetch(`${API_URL}/login/register-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      })
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            return response.json()
          }
          throw response
        })
        .then(resolve)
        .catch(reject)
    })
  }


  completeRegistration(payload) {
    return new Promise((resolve, reject) => {
      fetch(`${API_URL}/v1/user/complete-registration`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: this.getToken(),
          'Auth-SystemKey': '1',
          'Auth-ApiKey': '!@#ROXCLAPP!@#'
        },
        body: JSON.stringify(payload)
      })
        .then(response => {
          if (response.status >= 200 && response.status < 400) {
            return response.json()
          }
          throw response
        })
        .then(parsedResponse => {
          if (parsedResponse.authToken) {
            this.setToken(parsedResponse.authToken)
          }
          if (parsedResponse.refreshToken) {
            this.setRefreshToken(parsedResponse.refreshToken)
          }
          resolve(parsedResponse)
        })
        .catch(reject)
    })
  }

  refreshToken() {
    const token = this.getRefreshToken()
    if (!token) {
      return Promise.reject()
    }
    return fetch(`${API_URL}/v1/token/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        Refresh_token: token
      })
    })
      .then(response => {
        if (response.status >= 200 && response.status < 400) {
          return response.json()
        }
        return Promise.reject(response)
      })
      .then(res => {
        if (res.Authorization) {
          this.setToken(res.Authorization)
          return res
        } else {
          return Promise.reject(res)
        }
      })
  }

  /** ***** Logged In *****
   * Checks if there is a saved token
   */
  isLoggedIn() {
    const token = this.getToken()
    return !!token
  }

  /** ***** Is Token Expired *****
   * TODO: add logic to proactively get the refresh token before we get 401/403
   * Checks if token is expired
   */
  isTokenExpired(token = null) {
    token = token || this.getToken()
    try {
      const decoded = decode(token)
      if (decoded.exp < Date.now() / 1000) {
        return true
      } else {
        return false
      }
    } catch (err) {
      return false
    }
  }

  setToken(idToken) {
    localStorage.setItem('id_token', idToken)
  }
  setRefreshToken(refreshToken) {
    localStorage.setItem('rox_refresh_token', refreshToken)
  }

  getToken() {
    return localStorage.getItem('id_token')
  }
  getRefreshToken() {
    return localStorage.getItem('rox_refresh_token')
  }

  isLoggingOut() {
    return AuthService._loggingOut === true
  }

  logout() {
    // Guard against concurrent in-flight requests that each hit 401/403 and
    // try to log out again. Only the first call actually navigates and reloads;
    // subsequent calls are no-ops so we don't stack redirects / reloads.
    if (AuthService._loggingOut) {
      return
    }
    AuthService._loggingOut = true

    this.selectedClient = null
    localStorage.removeItem('id_token')
    localStorage.removeItem('rox_refresh_token')
    localStorage.removeItem('loggedInUser')
    localStorage.removeItem('rox_plaid_token')
    if (typeof window.Intercom === 'function') {
      window.Intercom('shutdown');
    }
    history.push('/login')
    window.location.reload(true)
    localStorage.removeItem('redirectUrl')
    localStorage.removeItem('pendingCredits')
    sessionStorage.removeItem('deepLinkRedirect')
  }

  getProfile() {
    return decode(this.getToken())
  }

  setPlaidLinkToken(token) {
    localStorage.setItem('rox_plaid_link_token', token)
  }

  getPlaidLinkToken() {
    localStorage.getItem('rox_plaid_link_token')
  }

  getClientFeatures() {
    const client = this.loggedInUser?.securityContext?.client;
    const roleName = this.loggedInUser?.securityContext?.roles[0]?.roleName;

    if (!client) {
      return {};
    }
  
    return {
      hideVerification: client.hideVerification || false,
      hideCreditCheck: client.hideCreditCheck || false,
      hideBankTransactions: client.hideBankTransactions || false,
      hideFmcsa: client.hideFmcsa || false,
      hideDecisioning: client.hideDecisioning || false,
      sosSearchEnabled: client.sosSearchEnabled || false,
      newUccEnabled: client.newUccEnabled || false,
      uccStatusNotificationEnabled: client.uccStatusNotificationEnabled || false,
      demoModeEnabled: client.demoModeEnabled || false,
      uccFilingRecordingTaxEnabled : client.uccFilingRecordingTaxEnabled || false,
      creditMemoEnabled : client.creditMemoEnabled || false,
      debtorUnderwritingEnabled: client.debtorUnderwritingEnabled || false,
      customFCSCredentialsRequired: client.customFCSCredentialsRequired || false,
      monitoringEnabled: client.monitoringEnabled || false,
      docusignEnabled: client.docusingEnabled || false,
      gmailPdfImportEnabled: client.gmailPdfImportEnabled || false,
      activityDashboardEnabled: client.activityDashboardEnabled || false,
      decisionSlaHours: client.decisionSlaHours || 24,
      isReadOnlyUser: roleName === 'READ_ONLY_USER' || false,
      creditUsageEnabled: client.creditUsageEnabled || false,
      hubSpotIntegrationEnabled: client.hubSpotIntegrationEnabled || false,
      sosAllStatesSearchEnabled: client.sosAllStatesSearchEnabled || false,
      marketplaceEnabled: client.marketplaceEnabled || false,
      fmcsaExtensiveSearchEnabled: client.fmcsaExtensiveSearchEnabled || false,
      reputationalAiAgent: client.featureFlags?.includes('REPUTATIONAL_AI_AGENT') || false,
      tinVerification: client.featureFlags?.includes('TIN_VERIFICATION') || false,
      motusEnabled: client.featureFlags?.includes('MOTUS') || false,
      samgovEnabled: client.featureFlags?.includes('SAMGOV') || false,
      nppesEnabled: client.featureFlags?.includes('NPPES') || false,
      leieEnabled: client.featureFlags?.includes('LEIE') || false,
      stateMedicaidEnabled: client.featureFlags?.includes('STATE_MEDICAID') || false,
      taxrockEnabled: client.featureFlags?.includes('TAXROCK') || false,
	    bankDocsAnalysis: client.featureFlags?.includes('BANK_DOC_ANALYSIS') || false,
      healthcareSearchEnabled: client.featureFlags?.includes('HEALTHCARE_SEARCH') || false,
      monitoringAdvancedEnabled: client.featureFlags?.includes('MONITORING_ADVANCED') || false,
      newApplicationDetail: client.featureFlags?.includes('NEW_APPLICATION_DETAIL') || false,
      balanceSheetAnalysis: client.featureFlags?.includes('BALANCE_SHEET_ANALYSIS') || false,
      incomeStatementAnalysis: client.featureFlags?.includes('INCOME_STATEMENT_ANALYSIS') || false,
      cashFlowStatementAnalysis: client.featureFlags?.includes('CASH_FLOW_STATEMENT_ANALYSIS') || false,
      dscrAnalysis: client.featureFlags?.includes('DSCR_ANALYSIS') || false,
      creditMemoApprovalEnabled: client.featureFlags?.includes('CREDIT_MEMO_APPROVAL') || false,
      applicationExportEnabled: client.featureFlags?.includes('APPLICATION_EXPORT') || false,
      applicationDeclineEmailEnabled: client.featureFlags?.includes('APPLICATION_DECLINE_EMAIL') || false,
      salesRepUsersEnabled: client.featureFlags?.includes('SALES_REP_USERS') || false,
      salesRepNoteEmailsEnabled: client.featureFlags?.includes('SALES_REP_NOTE_EMAILS') || false,
      applicationFlaggingEnabled: client.featureFlags?.includes('APPLICATION_FLAGGING') || false
    };
  }


}

export default new AuthService()
